import {
  convertPrice,
  readCatalogDisplayFxSnapshotForTrace,
} from "../services/currencyService";
import { getPackageProfilePriceDisplay } from "./packageProfilePriceDisplay";

function _isDev() {
  try {
    return import.meta.env?.DEV === true;
  } catch {
    return false;
  }
}

/** Same rounding/format as SummaryPriceCard / VP PDF ILS line (no catalogue FX). */
export function formatPackageMarketingTextPriceIls(amountIls) {
  const n = Number(amountIls);
  if (!Number.isFinite(n)) return "0₪";
  return `${Math.round(n).toLocaleString("en-US")}₪`;
}

function _legacyMarketingRawIls(ourPrice) {
  return (
    ourPrice?.priceWithCreditCardFee ||
    ourPrice?.priceWithHandleFee ||
    ourPrice?.priceWithCommission ||
    0
  );
}

function _resolveSummaryDisplayedTotalIls(effectiveOurPrice, billableAttractionsIls) {
  if (effectiveOurPrice?.priceWithCreditCardFee == null) return null;
  const billableExtra = Number(billableAttractionsIls) || 0;
  return Number(effectiveOurPrice.priceWithCreditCardFee) + billableExtra;
}

function _emitMarketingTextPriceTrace(payload) {
  if (!_isDev()) return;
  const record = {
    sessionId: "5d1b20",
    runId: payload.runId ?? "pre-fix",
    hypothesisId: payload.hypothesisId ?? "A",
    location: "resolvePackageMarketingTextDisplayPrice.js",
    message: "[MARKETING_TEXT_PRICE_TRACE]",
    data: payload,
    timestamp: Date.now(),
  };
  console.info("[MARKETING_TEXT_PRICE_TRACE]", payload);
  if (import.meta.env?.MODE === "test") return;
  // #region agent log
  if (typeof fetch === "function") {
    fetch("http://127.0.0.1:7890/ingest/b0b67dfb-7306-4d13-82f5-46e25cc15ee5", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Debug-Session-Id": "5d1b20",
      },
      body: JSON.stringify(record),
    }).catch(() => {});
  }
  // #endregion
}

/**
 * SSOT for package marketing-text price: same ILS total + formatting as order summary (getPackageProfilePriceDisplay).
 * Never uses convertPrice or catalogue currentRate.
 */
export function resolvePackageMarketingTextDisplayPrice({
  ourPrice = null,
  packageProfileDisplayOurPrice = null,
  billableAttractionsIls = 0,
  persistedPublicOfferIls = null,
  ilsToUsd = null,
  priceUnavailable = false,
  priceUnavailableLabel = null,
  orderId = null,
  packageId = null,
  runId = "pre-fix",
} = {}) {
  const profileOurPrice = packageProfileDisplayOurPrice ?? ourPrice;
  let effectiveOurPrice = profileOurPrice;

  if (persistedPublicOfferIls != null && Number.isFinite(Number(persistedPublicOfferIls))) {
    effectiveOurPrice = {
      ...(profileOurPrice ?? {}),
      priceWithCreditCardFee: Number(persistedPublicOfferIls),
    };
  }

  const uiDisplay = getPackageProfilePriceDisplay(effectiveOurPrice, ilsToUsd, {
    billableAttractionsIls,
    priceUnavailable,
    priceUnavailableLabel,
  });

  const legacyRaw = _legacyMarketingRawIls(ourPrice);
  const fxSnap = readCatalogDisplayFxSnapshotForTrace();
  const legacyFormatted = convertPrice(legacyRaw);
  const usedConvertPrice = Boolean(
    fxSnap.rate && fxSnap.rate !== "undefined" && fxSnap.currencyInfoText
  );

  if (priceUnavailable === true || effectiveOurPrice?.priceWithCreditCardFee == null) {
    const label =
      priceUnavailableLabel != null && String(priceUnavailableLabel).trim() !== ""
        ? String(priceUnavailableLabel).trim()
        : uiDisplay.cardPrimary ?? "מחיר לא זמין";
    _emitMarketingTextPriceTrace({
      runId,
      orderId,
      packageId,
      uiDisplayedPrice: uiDisplay.cardPrimary,
      marketingTextPriceBeforeFormat: null,
      sourceField: "priceUnavailable",
      currency: "ILS",
      currentRate: fxSnap.rate,
      usedConvertPrice,
      convertPriceInvoked: true,
      finalTextPrice: label,
      legacyMarketingRawIls: legacyRaw,
      legacyConvertPriceOutput: legacyFormatted,
    });
    return { priceStr: label, amountIls: null, sourceField: "priceUnavailable" };
  }

  const amountIls = _resolveSummaryDisplayedTotalIls(
    effectiveOurPrice,
    billableAttractionsIls
  );
  const priceStr = formatPackageMarketingTextPriceIls(amountIls);

  _emitMarketingTextPriceTrace({
    runId,
    orderId,
    packageId,
    uiDisplayedPrice: uiDisplay.cardPrimary,
    marketingTextPriceBeforeFormat: amountIls,
    sourceField: "ourPrice.priceWithCreditCardFee",
    currency: "ILS",
    currentRate: fxSnap.rate,
    usedConvertPrice,
    convertPriceInvoked: true,
    finalTextPrice: priceStr,
    legacyMarketingRawIls: legacyRaw,
    legacyConvertPriceOutput: legacyFormatted,
    billableAttractionsIls: Number(billableAttractionsIls) || 0,
  });

  return {
    priceStr,
    amountIls,
    sourceField: "ourPrice.priceWithCreditCardFee",
    uiDisplayedPrice: uiDisplay.cardPrimary,
  };
}
